/**
 * Resolves which co-brand the beta surfaces should render.
 *
 * The Brand Updates 2026 design carries a brand toggle (T.io Native / UPS /
 * Advocate) that rewrites six CSS custom properties — accent, on-accent,
 * surface-2, nav-active-bg, nav-active-tx, tio-mark. This service answers "which
 * brand", and `styles/beta/home.css` owns the values via
 * `[data-beta-brand="…"]`.
 *
 * SCOPE: this is the first piece of multi-brand theming in tio-employee. It is
 * intentionally a lookup table keyed on the partner subdomain rather than
 * backend-driven — there is no brand field on companySettings yet (see
 * BETA_HOME_GAPS.md, gap: brand-source). When one lands, only `brandFor` below
 * needs to change.
 */
import Service, { service } from '@ember/service';
import type PartnerService from 'tio-employee/services/partner';

export const BETA_BRANDS = ['tio', 'ups', 'advocate'] as const;

export type BetaBrand = (typeof BETA_BRANDS)[number];

/** Query param / localStorage key used to force a brand during design review. */
export const BETA_BRAND_OVERRIDE_KEY = 'tio-beta-brand';

/**
 * Partner subdomain → co-brand. Anything unlisted renders T.io Native, which is
 * the correct default for the ~200 partners with no bespoke brand.
 */
const SUBDOMAIN_BRANDS: Record<string, BetaBrand> = {
  ups: 'ups',
  advocate: 'advocate',
  advocatehealth: 'advocate',
};

function isBetaBrand(value: string | null): value is BetaBrand {
  return value !== null && (BETA_BRANDS as readonly string[]).includes(value);
}

export default class BetaBrandService extends Service {
  @service declare partner: PartnerService;

  /**
   * Brand implied by the current partner, ignoring any review override.
   */
  get brandForPartner(): BetaBrand {
    const subDomain = (this.partner.subDomain ?? '').toLowerCase();
    return SUBDOMAIN_BRANDS[subDomain] ?? 'tio';
  }

  /**
   * Brand actually rendered — the review override when one is set, else the
   * partner's brand.
   *
   * The override exists so design can review all three co-brands against real
   * data on one login. It is read-only from the app's perspective; set it from
   * the console with
   * `localStorage.setItem('tio-beta-brand', 'ups')` and reload.
   */
  get brand(): BetaBrand {
    return this.override ?? this.brandForPartner;
  }

  get override(): BetaBrand | null {
    try {
      const stored = localStorage.getItem(BETA_BRAND_OVERRIDE_KEY);
      return isBetaBrand(stored) ? stored : null;
    } catch {
      // Safari private mode throws on localStorage access; fall back to the
      // partner brand rather than breaking the page.
      return null;
    }
  }
}

declare module '@ember/service' {
  interface Registry {
    'beta-brand': BetaBrandService;
  }
}
